import React, { useState } from 'react';

interface CrowdfundingFullListProps {
  onOpenProject: (projectId: number) => void;
}

// Interfața pentru un proiect de crowdfunding
interface CrowdfundingProjectPreview {
  id: number;
  titlu: string;
  descriereScurta: string;
  imagineUrl: string;
  sumaStransa: number;
  sumaTarget: number;
  zileRamase: number;
  sustinatori: number;
  utilizator: string;
  companie: string;
  taguri: string[];
  categorie: string;
}

const CrowdfundingFullList: React.FC<CrowdfundingFullListProps> = ({ onOpenProject }) => {
  // State pentru căutare și filtrare
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('toate');
  
  // Categorii disponibile
  const categorii = [
    { id: 'toate', nume: 'Toate categoriile' },
    { id: 'tech', nume: 'Tehnologie' },
    { id: 'agricultura', nume: 'Agricultură' },
    { id: 'educatie', nume: 'Educație' },
    { id: 'servicii', nume: 'Servicii' },
    { id: 'productie', nume: 'Producție' },
  ];

  // Lista de proiecte extinsa
  const projects: CrowdfundingProjectPreview[] = [
    {
      id: 1,
      titlu: "Centru de inovație pentru antreprenorii din Vrancea",
      descriereScurta: "Un spațiu modern dedicat inovației și colaborării pentru comunitatea de afaceri din regiunea noastră.",
      imagineUrl: "/crowdfunding.png",
      sumaStransa: 35000,
      sumaTarget: 75000,
      zileRamase: 55,
      sustinatori: 47,
      utilizator: "Asociația Antreprenorilor din Vrancea",
      companie: "Patronat Vrancea",
      taguri: ["inovație", "startup", "coworking"],
      categorie: "servicii"
    },
    {
      id: 2,
      titlu: "Târg anual de produse locale - Vrancea Manufacturing",
      descriereScurta: "Organizarea unui târg anual care să promoveze producătorii și meșteșugarii locali la nivel național.",
      imagineUrl: "/crowdfunding.png",
      sumaStransa: 12500,
      sumaTarget: 30000,
      zileRamase: 21,
      sustinatori: 28,
      utilizator: "Ion Gheorghe",
      companie: "Camera de Comerț Vrancea",
      taguri: ["marketing", "evenimente", "tradiții"],
      categorie: "servicii"
    },
    {
      id: 3,
      titlu: "Program de digitalizare pentru micii producători",
      descriereScurta: "Asistență tehnică și cursuri pentru producătorii locali care vor să treacă la vânzare online și marketing digital.",
      imagineUrl: "/crowdfunding.png",
      sumaStransa: 8750,
      sumaTarget: 25000,
      zileRamase: 42,
      sustinatori: 15,
      utilizator: "Tech Hub Vrancea",
      companie: "Asociația IT Vrancea",
      taguri: ["digitalizare", "educație", "e-commerce"],
      categorie: "tech"
    },
    {
      id: 4,
      titlu: "Platformă de analiză a solului pentru agricultori",
      descriereScurta: "Dezvoltarea unei tehnologii inovatoare care permite agricultorilor să analizeze calitatea solului direct în câmp folosind dispozitive mobile.",
      imagineUrl: "/crowdfunding.png",
      sumaStransa: 18600,
      sumaTarget: 45000,
      zileRamase: 35,
      sustinatori: 23,
      utilizator: "AgroTech Vrancea",
      companie: "Asociația Fermierilor Vrânceni",
      taguri: ["agricultură", "tehnologie", "inovație"],
      categorie: "agricultura"
    },
    {
      id: 5,
      titlu: "Laborator mobil pentru educație STEM în școlile rurale",
      descriereScurta: "Un laborator mobil care va vizita școlile din zonele rurale pentru a oferi elevilor acces la experimente și activități practice în domeniul științei și tehnologiei.",
      imagineUrl: "/crowdfunding.png",
      sumaStransa: 15200,
      sumaTarget: 35000,
      zileRamase: 28,
      sustinatori: 34,
      utilizator: "Educație pentru Viitor",
      companie: "Fundația Educațională Vrancea",
      taguri: ["educație", "STEM", "rural"],
      categorie: "educatie"
    },
    {
      id: 6,
      titlu: "Linie de producție pentru ambalaje ecologice",
      descriereScurta: "Implementarea unei linii de producție pentru ambalaje biodegradabile destinate producătorilor locali de alimente și băuturi.",
      imagineUrl: "/crowdfunding.png",
      sumaStransa: 28500,
      sumaTarget: 60000,
      zileRamase: 45,
      sustinatori: 19,
      utilizator: "EcoPack SRL",
      companie: "Asociația Producătorilor Eco",
      taguri: ["ecologie", "producție", "sustenabilitate"],
      categorie: "productie"
    },
    {
      id: 7,
      titlu: "Aplicație pentru promovarea turismului local",
      descriereScurta: "O platformă digitală care să conecteze turiștii cu atracțiile, serviciile și evenimentele din județul Vrancea.",
      imagineUrl: "/crowdfunding.png",
      sumaStransa: 9800,
      sumaTarget: 20000,
      zileRamase: 25,
      sustinatori: 42,
      utilizator: "Visit Vrancea",
      companie: "Asociația de Turism Vrancea",
      taguri: ["turism", "digitalizare", "marketing"],
      categorie: "tech"
    },
    {
      id: 8,
      titlu: "Centru de formare pentru meserii tradiționale",
      descriereScurta: "Un spațiu dedicat învățării și păstrării meșteșugurilor tradiționale vrâncene, cu focus pe ceramică, țesut și prelucrarea lemnului.",
      imagineUrl: "/crowdfunding.png",
      sumaStransa: 21500,
      sumaTarget: 40000,
      zileRamase: 60,
      sustinatori: 31,
      utilizator: "Meșteșuguri Vrâncene",
      companie: "Centrul Cultural Tradițional",
      taguri: ["tradiții", "educație", "meșteșuguri"],
      categorie: "educatie"
    }
  ];

  // Filtrarea proiectelor
  const filteredProjects = projects.filter((project) => {
    const matchesSearch = project.titlu.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         project.descriereScurta.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'toate' || project.categorie === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b">
        <h2 className="text-xl font-semibold">Proiecte de crowdfunding</h2>
        <p className="text-gray-600">Descoperă și susține inițiativele antreprenoriale din comunitatea noastră</p>
      </div>

      {/* Bara de căutare și filtrare */}
      <div className="p-4 border-b">
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Caută proiecte..."
                className="w-full p-2 pl-8 border rounded-md"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute left-3 top-3 text-gray-400">
                🔍
              </div>
            </div>
          </div>
          <div className="flex-1 md:flex-initial">
            <select
              className="w-full p-2 border rounded-md bg-white"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categorii.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.nume}</option>
              ))}
            </select>
          </div>
          <div>
            <button 
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium flex items-center"
            >
              <span className="mr-2">➕</span>
              Inițiază un proiect
            </button>
          </div>
        </div>
      </div>

      {/* Grid de proiecte */}
      <div className="p-4">
        {filteredProjects.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="text-5xl mb-4">💰</div>
            <p>Nu a fost găsit niciun proiect care să corespundă criteriilor tale de căutare.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {filteredProjects.map(project => (
              <div 
                key={project.id}
                className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => onOpenProject(project.id)}
              >
                <div className="relative">
                  <img 
                    src={project.imagineUrl} 
                    alt={project.titlu}
                    className="w-full h-40 object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                    <div className="text-white text-xs">
                      Încă {project.zileRamase} zile
                    </div>
                  </div>
                </div>
                
                <div className="p-3">
                  <h3 className="font-semibold text-lg mb-2 line-clamp-2 text-blue-600">{project.titlu}</h3>
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">{project.descriereScurta}</p>
                  
                  <div className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{Math.round((project.sumaStransa / project.sumaTarget) * 100)}%</span>
                      <span>{project.sumaStransa} lei</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div 
                        className="bg-blue-600 h-1.5 rounded-full" 
                        style={{width: `${Math.min(Math.round((project.sumaStransa / project.sumaTarget) * 100), 100)}%`}}
                      ></div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-1 mb-3">
                    {project.taguri.slice(0, 3).map((tag, index) => (
                      <span key={index} className="px-2 py-0.5 bg-gray-100 text-xs rounded-md">
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{project.sustinatori} susținători</span>
                    <span>Inițiat de {project.companie}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Paginare */}
      <div className="p-4 border-t">
        <div className="flex justify-center">
          <div className="flex space-x-1">
            <button className="px-3 py-1 border rounded-md text-gray-600 hover:bg-gray-100">&laquo;</button>
            <button className="px-3 py-1 border rounded-md bg-blue-600 text-white">1</button>
            <button className="px-3 py-1 border rounded-md text-gray-600 hover:bg-gray-100">2</button>
            <button className="px-3 py-1 border rounded-md text-gray-600 hover:bg-gray-100">3</button>
            <button className="px-3 py-1 border rounded-md text-gray-600 hover:bg-gray-100">&raquo;</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrowdfundingFullList;